import { Card, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useMemo } from 'react';
import { OmsOrderDetail, OmsOrderItem } from 'types/oms';
import LOCALS from 'commons/locals';
import { Trans } from 'react-i18next';
import LinkButton from 'components/link-button';
import {
  CHECKOUT_OUT_PRODUCT_PRICE_TYPE,
  CHECKOUT_OUT_PRODUCT_PRICE_TYPE_OPTION_LIST,
  findLabelByValue,
  PMS_PRODUCT_STOCK_STATUS_OPTION_LIST,
} from 'commons/options';
import { ProductUpdateInfoNewType } from 'types/pms';
import i18n from 'i18n';
import { Package } from 'lucide-react';
import { Image } from 'antd';

type OrderItemListProps = {
  omsOrderDetail: OmsOrderDetail;
  productList: ProductUpdateInfoNewType[];
};

const OrderItemList = ({
  omsOrderDetail: { orderItemList },
  productList,
}: OrderItemListProps) => {
  const columns: ColumnsType<OmsOrderItem> = useMemo(() => {
    return [
      {
        title: <Trans i18nKey={LOCALS.sales_product_id} />,
        dataIndex: 'productId',
        key: 'productId',
        render: (productId: number) => {
          return (
            <LinkButton href={`/pms/product-view/${productId}`}>
              {productId}
            </LinkButton>
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.SYLYoWTeQq} />,
        dataIndex: 'productSnDes',
        key: 'productSnDes',
        render: (
          productSnDes: OmsOrderItem['productSnDes'],
          record: OmsOrderItem
        ) => {
          const { productId } = record;
          const target = productList.find((i) => i.id === productId);
          if (target && target.description) {
            return target.description;
          }

          return productSnDes;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.product_pictures} />,
        dataIndex: 'productPic',
        key: 'productPic',
        render: (
          productPic: OmsOrderItem['productPic'],
          record: OmsOrderItem
        ) => {
          const { productId } = record;
          const target = productList.find((i) => i.id === productId);
          if (target && target.albumPics) {
            const picList = target.albumPics.split(',');
            const pic = picList[0];

            if (pic) {
              return <Image width={48} src={pic} />;
            }
          }

          return productPic ? <Image width={48} src={productPic} /> : '-';
        },
      },
      {
        title: <Trans i18nKey={LOCALS.product_name} />,
        dataIndex: 'productName',
        key: 'productName',
      },
      {
        title: <Trans i18nKey={LOCALS.price} />,
        dataIndex: 'productPriceActualCurrency',
        key: 'productPriceActualCurrency',
        render: (
          productPriceActualCurrency: OmsOrderItem['productPriceActualCurrency'],
          { actualCurrency }: OmsOrderItem
        ) => {
          return `${actualCurrency} ${productPriceActualCurrency?.toLocaleString()}`;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.quantity} />,
        dataIndex: 'productQuantity',
        key: 'productQuantity',
        render: (productQuantity: OmsOrderItem['productQuantity']) => {
          return productQuantity;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.price_type} />,
        dataIndex: 'isTaxFree',
        key: 'isTaxFree',
        render: (isTaxFree: OmsOrderItem['isTaxFree']) => {
          return findLabelByValue(
            isTaxFree
              ? CHECKOUT_OUT_PRODUCT_PRICE_TYPE.WITH_OUT_TAX
              : CHECKOUT_OUT_PRODUCT_PRICE_TYPE.WITH_TAX,
            CHECKOUT_OUT_PRODUCT_PRICE_TYPE_OPTION_LIST
          );
        },
      },
      {
        title: i18n.t(LOCALS.uMihwkqqts),
        dataIndex: 'productId',
        key: 'stockStatus',
        render(productId: OmsOrderItem['productId']) {
          const target = productList.find((i) => i.id === productId);
          if (!target) return <div>-</div>;

          return (
            <div>
              {findLabelByValue(
                target.stockStatus,
                PMS_PRODUCT_STOCK_STATUS_OPTION_LIST
              )}
            </div>
          );
        },
      },
    ];
  }, [productList]);

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex items-center gap-2 mb-4 text-base font-medium">
        <Package className="w-5 h-5" />
        <Trans i18nKey={LOCALS.product_info} />
      </div>

      <Table
        size="small"
        bordered
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        pagination={false}
        rowKey={'id'}
        dataSource={orderItemList}
        columns={columns}
      ></Table>
    </Card>
  );
};

export default OrderItemList;
